const MODEL_URL = "https://cdn.jsdelivr.net/npm/@vladmandic/face-api@1.7.15/model/";
const DETECT_SIZE = 640;

let faceApiPromise = null;

function loadFaceApi() {
  if (!faceApiPromise) {
    faceApiPromise = (async () => {
      const faceapi = await import("@vladmandic/face-api");
      try {
        await faceapi.tf.setBackend("webgl");
      } catch (_) {
        await faceapi.tf.setBackend("cpu");
      }
      await faceapi.tf.ready();
      await Promise.all([
        faceapi.nets.ssdMobilenetv1.loadFromUri(MODEL_URL),
        faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
        faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL),
      ]);
      return faceapi;
    })().catch((err) => {
      faceApiPromise = null;
      throw err;
    });
  }
  return faceApiPromise;
}

/** Start downloading the face models in the background. */
export function preloadFaceModels() {
  loadFaceApi().catch(() => {});
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Could not read the image"));
    img.src = src;
  });
}

function drawToCanvas(img, maxSize, allowEnlarge) {
  const scale = allowEnlarge
    ? maxSize / Math.max(img.width, img.height)
    : Math.min(1, maxSize / Math.max(img.width, img.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(img.width * scale);
  canvas.height = Math.round(img.height * scale);
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  return canvas;
}

export function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("Could not read the file"));
    reader.readAsDataURL(file);
  });
}

/** Normalises any photo (camera or upload) to a small JPEG data URL suitable for storage and checks. */
export async function compressPhoto(dataUrl, maxSize = 800, quality = 0.85) {
  const img = await loadImage(dataUrl);
  return drawToCanvas(img, maxSize, false).toDataURL("image/jpeg", quality);
}

export function dataUrlToBlob(dataUrl) {
  const [header, data] = dataUrl.split(",");
  const mime = (header.match(/data:(.*?);/) || [])[1] || "image/jpeg";
  const bytes = atob(data);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
  return new Blob([arr], { type: mime });
}

/**
 * Detects faces and returns the 128-number descriptor of the largest face.
 * Same pipeline as the server-side member index (640px, SSD 0.5 then 0.3).
 */
export async function analyseFace(dataUrl) {
  const faceapi = await loadFaceApi();
  const img = await loadImage(dataUrl);
  const canvas = drawToCanvas(img, DETECT_SIZE, true);
  let faces = [];
  for (const minConfidence of [0.5, 0.3]) {
    faces = await faceapi
      .detectAllFaces(canvas, new faceapi.SsdMobilenetv1Options({ minConfidence }))
      .withFaceLandmarks()
      .withFaceDescriptors();
    if (faces.length) break;
  }
  if (!faces.length) return { faces: 0, descriptor: null };
  faces.sort((a, b) => b.detection.box.area - a.detection.box.area);
  const main = faces[0];
  const areaRatio = main.detection.box.area / (canvas.width * canvas.height);
  const others = faces.slice(1).filter((f) => f.detection.box.area > main.detection.box.area * 0.35);
  return {
    faces: 1 + others.length,
    tooSmall: areaRatio < 0.03,
    descriptor: Array.from(main.descriptor).map((v) => Math.round(v * 1e6) / 1e6),
  };
}
